import { useCallback } from 'react';
import { Checkbox, Flex, Typography } from '@banhala/ui-kit';
import { type NavigationProp, useNavigation } from '@react-navigation/native';
import type { OneCheckboxBody } from '../types';
import type { AnyObject } from '../../../../../types';
import { bridge } from '../../../../../utils';
import { useFilterListAtom } from '../Provider/filterList/useFilterListAtom';

type InlineOneCheckboxProps = {
  oneCheckbox: OneCheckboxBody;
  logging: AnyObject;
  scrollToFilterList: VoidFunction;
};

/**
 * FILTER_LIST body 내장 one_checkbox 렌더링
 * - iOS: MetaFeedOneCheckboxFilterItemReactor
 * - Android: ComponentFilterOneCheckboxState
 */
export const InlineOneCheckbox = ({
  oneCheckbox,
  logging,
  scrollToFilterList,
}: InlineOneCheckboxProps) => {
  const navigation = useNavigation<
    NavigationProp<{ [route: string]: Record<string, string[] | undefined> }>
  >();
  const { setFilterListAtom } = useFilterListAtom();
  const { item } = oneCheckbox;

  const handleToggle = useCallback(() => {
    const nextIsOn = !item.is_on;

    // CLICK_CHECKBOX_FILTER (iOS/Android 동일)
    bridge.logAnalyticsEvent({
      name: 'CLICK_CHECKBOX_FILTER',
      analytics: {
        ...logging?.analytics,
        ...oneCheckbox.logging?.analytics,
        IS_ON: nextIsOn,
      },
      ably_only_log: {
        ...logging?.ably_only_log,
        ...oneCheckbox.logging?.ably_only_log,
      },
    });

    scrollToFilterList();

    // 필터 적용
    const nextValue = nextIsOn ? [`${item.query_value}`] : undefined;
    setFilterListAtom(prev => {
      const next = { ...prev, [item.query_name]: nextValue };
      // 체크 해제 시 연관 필터도 함께 제거 (delete_dependency_query_map)
      if (!nextIsOn && item.delete_dependency_query_map) {
        for (const key of Object.keys(item.delete_dependency_query_map)) {
          delete next[key];
        }
      }
      return next;
    });
    navigation.setParams({ [item.query_name]: nextValue });

    // RECEIVE_INFO_FROM_CHECKBOX_FILTER (서버 응답 후 발화)
    if (oneCheckbox.receive_logging) {
      bridge.logAnalyticsEvent({
        name: 'RECEIVE_INFO_FROM_CHECKBOX_FILTER',
        analytics: oneCheckbox.receive_logging.analytics as Record<string, unknown>,
        ably_only_log: oneCheckbox.receive_logging.ably_only_log as Record<string, unknown>,
      });
    }
  }, [item, logging, oneCheckbox, scrollToFilterList, setFilterListAtom, navigation]);

  return (
    <Flex.Row
      alignItems="center"
      padding={{ horizontal: 16 }}
      height={36}
      gap={4}
      onPress={handleToggle}
    >
      <Checkbox value={item.is_on} onPress={handleToggle} />
      <Typography
        type="body2"
        color={item.is_on ? 'content_pink_primary' : 'content_tertiary'}
      >
        {item.title}
      </Typography>
    </Flex.Row>
  );
};
